// ✅ ProductionBOMReport.jsx (FULL UPDATED CODE)
// Changes made:
// 1) Safe localStorage helper added
// 2) toInt / toStr helpers added
// 3) branchid / branchtype / userid / finYear values read safely
// 4) commonHeaders added
// 5) API calls now send headers: commonHeaders
// 6) existing functionality kept same

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaChevronDown, FaChevronUp } from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const ProductionBOMReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // -----------------------------
  // ✅ Helpers
  // -----------------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined && val !== "" ? val : fallback;
  };

  const toInt = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const toStr = (v, fallback = "") => {
    if (v === null || v === undefined) return fallback;
    const s = String(v).trim();
    return s !== "" ? s : fallback;
  };

  // -----------------------------
  // ✅ localStorage values
  // -----------------------------
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");

  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // -----------------------------
  // ✅ parsed values
  // -----------------------------
  const branchid = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchtype = useMemo(() => toStr(branchTypeLS, "HO"), [branchTypeLS]);
  const userid = useMemo(() => toInt(userIdLS, 1), [userIdLS]);

  // -----------------------------
  // ✅ common headers
  // -----------------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": toStr(finYearId, ""),
      "x-finyear": toStr(finYear, ""),
      "x-branchid": branchid,
      "x-branchtype": branchtype,
      "x-userid": userid,
    }),
    [finYearId, finYear, branchid, branchtype, userid]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // ✅ dropdown data from API
  const [projectOptions, setProjectOptions] = useState([]);
  const [productionOptions, setProductionOptions] = useState([]);

  const [formData, setFormData] = useState({
    fromDate: "01/01/2021",
    toDate: "30/12/2025",
    projectid: "",
    productionid: "",
  });

  const [bomData, setBomData] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ---------------------------
  // ✅ Dropdown APIs
  // ---------------------------
  const loadProjects = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/bomlisting/projects`, {
        params: { branchid, loginuserid: userid },
        headers: commonHeaders,
      });
      setProjectOptions(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load projects");
      setProjectOptions([]);
    }
  };

  const loadProductions = async (projectid) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/bomlisting/productions`, {
        params: {
          branchid,
          loginuserid: userid,
          projectid: Number(projectid || 0),
        },
        headers: commonHeaders,
      });
      setProductionOptions(res.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load productions");
      setProductionOptions([]);
    }
  };

  // Initial load
  useEffect(() => {
    loadProjects();
    loadProductions(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchid, userid]);

  // When project changes -> reload productions + reset selected production
  useEffect(() => {
    setFormData((prev) => ({ ...prev, productionid: "" }));
    loadProductions(formData.projectid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectid]);

  // ---------------------------
  // ✅ SEARCH API
  // ---------------------------
  const handleSearch = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/bomlisting/search`, {
        params: {
          branchid,
          loginuserid: userid,
          fromDate: formData.fromDate,
          toDate: formData.toDate,
          projectid: Number(formData.projectid || 0),
          productionid: Number(formData.productionid || 0),
        },
        headers: commonHeaders,
      });

      const data = res.data?.data || [];
      setBomData(data);

      if (data.length === 0) {
        toast.info("No Records found");
      } else {
        toast.success(`Found ${data.length} record(s)`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to fetch BOM listing");
      setBomData([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // ✅ Print handlers
  // ---------------------------
  const handlePrintReport = (row) => {
    const bomid = row.bomId ?? row.BOMID ?? row.fld_BOMID ?? row.BomId ?? row.fld_BomId;
    if (!bomid) {
      toast.error("BOM ID missing in this row");
      return;
    }

    window.open(
      `/TarMixing/Tar_BOM_Report_Print?bomid=${bomid}&branchid=${branchid}`,
      "_blank"
    );
  };

  const handlePrintStock = (row) => {
    const bomid = row.bomId ?? row.BOMID ?? row.fld_BOMID ?? row.BomId ?? row.fld_BomId;
    if (!bomid) {
      toast.error("BOM ID missing in this row");
      return;
    }

    window.open(
      `/TarMixing/Tar_BOM_Report_StockPrint?bomid=${bomid}&branchid=${branchid}`,
      "_blank"
    );
  };

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION BOM LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Production BOM Listing
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    className={`block mb-2 text-sm font-medium ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    From Date
                  </label>
                  <input
                    type="text"
                    value={formData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block mb-2 text-sm font-medium ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    From Project
                  </label>
                  <select
                    value={formData.projectid}
                    onChange={(e) => handleInputChange("projectid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {projectOptions.map((p, idx) => (
                      <option key={p.id ?? p.projectid ?? idx} value={p.id ?? p.projectid}>
                        {p.name ?? p.projectName ?? p.projectname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    className={`block mb-2 text-sm font-medium ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    To Date
                  </label>
                  <input
                    type="text"
                    value={formData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    className={`block mb-2 text-sm font-medium ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Production Name
                  </label>
                  <select
                    value={formData.productionid}
                    onChange={(e) => handleInputChange("productionid", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {productionOptions.map((p, idx) => (
                      <option
                        key={p.id ?? p.productionid ?? idx}
                        value={p.id ?? p.productionid}
                      >
                        {p.name ?? p.productionName ?? p.productionname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center items-center mt-6 gap-4 p-2">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch /> {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {bomData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <span className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No Records found
                </span>
              </div>
            ) : (
              <div className={`overflow-x-auto ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <table className="min-w-full text-sm">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center">S.no</th>
                      <th className="px-4 py-3 text-center">Project Name</th>
                      <th className="px-4 py-3 text-center">Date</th>
                      <th className="px-4 py-3 text-center">Production Code</th>
                      <th className="px-4 py-3 text-center">Production Name</th>
                      <th className="px-4 py-3 text-center">Quantity</th>
                      <th className="px-4 py-3 text-center">Report</th>
                      <th className="px-4 py-3 text-center">Stock Report</th>
                    </tr>
                  </thead>

                  <tbody>
                    {bomData.map((row, idx) => {
                      const projectName =
                        row.ProjectName ?? row.projectName ?? row.fld_ProjectName ?? "";
                      const productionName =
                        row.ProductionName ?? row.productionName ?? row.fld_ProductionName ?? "";
                      const productionCode =
                        row.ProductionCode ?? row.productionCode ?? row.fld_ProductionCode ?? "";
                      const qty =
                        row.productionQty ?? row.quantity ?? row.fld_Quantity ?? row.qty ?? "";
                      const dateRaw =
                        row.entryDate ?? row.date ?? row.fld_Date ?? row.fld_date ?? "";

                      let dateText = "";
                      if (typeof dateRaw === "string") {
                        dateText = dateRaw.slice(0, 10);
                      } else if (dateRaw) {
                        const d = new Date(dateRaw);
                        dateText = Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
                      }

                      return (
                        <tr
                          key={row.BomId ?? row.bomId ?? row.fld_BomId ?? idx}
                          className={`border-t ${
                            isDark ? "border-gray-700" : "border-gray-200"
                          } ${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-900/30"
                              : "bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3 text-center">{idx + 1}</td>
                          <td className="px-4 py-3 text-center">{projectName}</td>
                          <td className="px-4 py-3 text-center">{dateText}</td>
                          <td className="px-4 py-3 text-center">{productionCode}</td>
                          <td className="px-4 py-3 text-center">{productionName}</td>
                          <td className="px-4 py-3 text-center">{qty}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handlePrintReport(row)}
                              className="text-blue-600 hover:underline"
                            >
                              Print
                            </button>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handlePrintStock(row)}
                              className="text-blue-600 hover:underline"
                            >
                              Print
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default ProductionBOMReport;